import React, { useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { formatApiError } from "@/lib/api";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { Card } from "@/components/ui/card";
import { GraduationCap } from "lucide-react";
import { toast } from "sonner";

export default function Login() {
  const { login } = useAuth();
  const nav = useNavigate();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [busy, setBusy] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      const user = await login(email, password);
      toast.success("Welcome back!");
      if (user.role === "super_admin") nav("/super");
      else if (user.role === "school_admin") nav("/school");
      else if (user.role === "teacher") nav("/teacher");
      else if (user.role === "parent") nav("/parent");
      else if (user.role === "student") nav("/student");
      else nav("/");
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || "Login failed");
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="min-h-screen flex bg-[#F8F9FA]">
      <div className="hidden lg:flex flex-1 relative bg-slate-900 text-white items-center justify-center p-16">
        <img src="https://images.pexels.com/photos/2982449/pexels-photo-2982449.jpeg?auto=compress&cs=tinysrgb&w=1200" alt="" className="absolute inset-0 w-full h-full object-cover opacity-30" />
        <div className="relative max-w-md">
          <Link to="/" className="flex items-center gap-2">
            <div className="w-10 h-10 rounded bg-amber-400 flex items-center justify-center"><GraduationCap className="w-6 h-6 text-slate-900" /></div>
            <span className="font-black text-2xl">School Dial</span>
          </Link>
          <h2 className="mt-16 text-4xl font-black tracking-tighter">Manage your school<br />in one place.</h2>
          <p className="mt-4 text-slate-300">Admissions, attendance, fees, exams and WhatsApp messaging — built for Indian schools.</p>
        </div>
      </div>
      <div className="flex-1 flex items-center justify-center p-6">
        <Card className="w-full max-w-md p-8">
          <h1 className="text-2xl font-bold text-slate-900">Log in to School Dial</h1>
          <p className="text-slate-500 text-sm mt-1">Super admins, school admins, teachers & parents</p>
          <form onSubmit={submit} className="mt-8 space-y-4" data-testid="login-form">
            <div>
              <Label htmlFor="email" className="mb-2 block">Email</Label>
              <Input id="email" type="email" required value={email} onChange={(e) => setEmail(e.target.value)} data-testid="login-email" />
            </div>
            <div>
              <Label htmlFor="password" className="mb-2 block">Password</Label>
              <Input id="password" type="password" required value={password} onChange={(e) => setPassword(e.target.value)} data-testid="login-password" />
            </div>
            <Button type="submit" disabled={busy} className="w-full bg-slate-900 hover:bg-slate-800" data-testid="login-submit">
              {busy ? "Signing in…" : "Log in"}
            </Button>
          </form>
          <div className="mt-4 text-center text-sm">
            <Link to="/" className="text-slate-500 hover:text-slate-900">← Back to home</Link>
          </div>
        </Card>
      </div>
    </div>
  );
}
